import React, { useState, useEffect } from 'react';
import { 
  BarChart3, PieChart, TrendingUp, TrendingDown, AlertTriangle, 
  Loader2, CheckCircle, XCircle, Clock, Gavel, DollarSign,
  Users, Target, Award, ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { morosidadesAPI } from '../api';
import { toast } from '../hooks/use-toast';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2 
  }).format(amount || 0);
};

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const COLORES_ESTADO = {
  activo: { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-700' },
  en_negociacion: { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-700' },
  judicial: { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-700' },
  cerrado: { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700' }
};

const COLORES_ANTIGUEDAD = ['#22C55E', '#EAB308', '#F97316', '#EF4444'];

const EstadisticasMorosidad = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    resumen: {
      total_casos: 0,
      casos_cerrados: 0,
      casos_activos: 0,
      en_judicial: 0,
      total_deuda: 0,
      total_cobrado: 0,
      deuda_pendiente: 0,
      deuda_en_judicial: 0,
      tasa_exito: 0,
      tasa_cobro: 0
    },
    por_estado: [],
    por_mes: [],
    antiguedad_deuda: [],
    top_deudores: []
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await morosidadesAPI.getEstadisticasCompletas();
      setStats(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las estadísticas',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const maxDeudaEstado = Math.max(...(stats.por_estado || []).map(e => e.pendiente), 1);
  const maxDeudaMes = Math.max(...(stats.por_mes || []).map(m => m.deuda), 1);
  const maxDeudaAntiguedad = Math.max(...(stats.antiguedad_deuda || []).map(a => a.pendiente), 1);
  const maxDeudaTop = Math.max(...(stats.top_deudores || []).map(d => d.deuda), 1);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const { resumen } = stats;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/contabilidad/morosidad')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-red-600" />
              Estadísticas de Morosidad
            </h1>
            <p className="text-gray-600 mt-1">Análisis del estado de reclamaciones y cobros</p>
          </div>
        </div>
      </div>

      {/* KPIs Principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Deuda Pendiente</p>
                <p className="text-2xl font-bold">{formatCurrency(resumen.deuda_pendiente)}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Cobrado</p>
                <p className="text-2xl font-bold">{formatCurrency(resumen.total_cobrado)}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Tasa de Éxito</p>
                <p className="text-2xl font-bold">{resumen.tasa_exito}%</p>
                <p className="text-xs text-blue-200">{resumen.casos_cerrados} de {resumen.total_casos} cerrados</p>
              </div>
              <Award className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Tasa de Cobro</p>
                <p className="text-2xl font-bold">{resumen.tasa_cobro}%</p>
                <p className="text-xs text-purple-200">del total reclamado</p>
              </div>
              <Target className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resumen de Casos */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-3xl font-bold">{resumen.total_casos}</p>
            <p className="text-sm text-gray-600">Total Casos</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-yellow-600">{resumen.casos_activos}</p>
            <p className="text-sm text-gray-600">En Tramitación</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4 text-center">
            <Gavel className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-purple-600">{resumen.en_judicial}</p>
            <p className="text-sm text-gray-600">En Judicial</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-green-600">{resumen.casos_cerrados}</p>
            <p className="text-sm text-gray-600">Cerrados</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4 text-center">
            <DollarSign className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-xl font-bold text-red-600">{formatCurrency(resumen.deuda_en_judicial)}</p>
            <p className="text-sm text-gray-600">Deuda en Judicial</p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Por Estado */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChart className="w-5 h-5 text-blue-600" />
              Distribución por Estado
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.por_estado && stats.por_estado.length > 0 ? (
              <div className="space-y-4">
                {stats.por_estado.map((item, index) => {
                  const colors = COLORES_ESTADO[item.estado] || COLORES_ESTADO.activo;
                  const estadoLabel = {
                    activo: 'Activo',
                    en_negociacion: 'En Negociación',
                    judicial: 'En Vía Judicial',
                    cerrado: 'Cerrado'
                  };
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Badge className={`${colors.light} ${colors.text}`}>
                            {estadoLabel[item.estado] || item.estado}
                          </Badge>
                          <span className="text-sm text-gray-500">({item.cantidad} casos)</span>
                        </div>
                        <span className="text-sm font-medium">{formatCurrency(item.pendiente)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full ${colors.bg} transition-all duration-500`}
                          style={{ width: `${(item.pendiente / maxDeudaEstado) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Deuda: {formatCurrency(item.deuda)}</span>
                        <span>Cobrado: {formatCurrency(item.pagos)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
            )}
          </CardContent>
        </Card>

        {/* Antigüedad de la Deuda */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-orange-600" />
              Antigüedad de la Deuda Pendiente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.antiguedad_deuda && stats.antiguedad_deuda.length > 0 ? (
              <div className="space-y-4">
                {stats.antiguedad_deuda.map((item, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORES_ANTIGUEDAD[index] }}
                        />
                        <span className="text-sm font-medium">{item.rango}</span>
                        <span className="text-sm text-gray-500">({item.cantidad} casos)</span>
                      </div>
                      <span className="text-sm font-bold">{formatCurrency(item.pendiente)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="h-3 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(item.pendiente / maxDeudaAntiguedad) * 100}%`,
                          backgroundColor: COLORES_ANTIGUEDAD[index]
                        }}
                      />
                    </div>
                  </div>
                ))}
                
                {/* Leyenda */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">
                    <strong>Análisis:</strong> Las deudas con mayor antigüedad (&gt;180 días) 
                    tienen menor probabilidad de cobro. Se recomienda priorizar la gestión 
                    de deudas recientes y considerar acciones judiciales para las más antiguas.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Evolución Mensual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Evolución Mensual de Nuevas Reclamaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.por_mes && stats.por_mes.length > 0 ? (
            <div className="h-64">
              <div className="flex items-end justify-between h-full gap-2">
                {stats.por_mes.map((item, index) => {
                  const altura = (item.deuda / maxDeudaMes) * 100;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div className="text-xs text-gray-500 mb-1">{item.cantidad}</div>
                      <div 
                        className="w-full bg-gradient-to-t from-red-500 to-red-400 rounded-t transition-all duration-500 relative group"
                        style={{ height: `${Math.max(altura, 5)}%` }}
                      >
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {formatCurrency(item.deuda)}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {MESES[item.month - 1]} {item.year?.toString().slice(-2)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No hay datos disponibles</p>
          )}
        </CardContent>
      </Card>

      {/* Top Deudores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Top 10 Deudores con Mayor Deuda Pendiente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.top_deudores && stats.top_deudores.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Deudor</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Deuda Pendiente</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">% del Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {stats.top_deudores.map((deudor, index) => {
                    const porcentaje = resumen.deuda_pendiente > 0 
                      ? ((deudor.deuda / resumen.deuda_pendiente) * 100).toFixed(1)
                      : 0;
                    const colors = COLORES_ESTADO[deudor.estado] || COLORES_ESTADO.activo;
                    const estadoLabel = {
                      activo: 'Activo',
                      en_negociacion: 'En Negociación',
                      judicial: 'Judicial',
                      cerrado: 'Cerrado'
                    };
                    
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index < 3 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{deudor.nombre}</p>
                          {deudor.judicial && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs mt-1">
                              <Gavel className="w-3 h-3 mr-1" /> Judicial
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-red-600">
                          {formatCurrency(deudor.deuda)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${colors.light} ${colors.text}`}>
                            {estadoLabel[deudor.estado] || deudor.estado}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-red-500"
                                style={{ width: `${(deudor.deuda / maxDeudaTop) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-12">{porcentaje}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No hay deudores pendientes</p>
          )}
        </CardContent>
      </Card>

      {/* Resumen Financiero */}
      <Card className="bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Resumen Financiero
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-gray-400 text-sm">Total Reclamado</p>
              <p className="text-3xl font-bold">{formatCurrency(resumen.total_deuda)}</p>
            </div>
            <div className="text-center border-x border-gray-700">
              <p className="text-gray-400 text-sm">Total Recuperado</p>
              <p className="text-3xl font-bold text-green-400">{formatCurrency(resumen.total_cobrado)}</p>
              <p className="text-sm text-green-300">{resumen.tasa_cobro}% del total</p>
            </div>
            <div className="text-center">
              <p className="text-gray-400 text-sm">Pendiente de Cobro</p>
              <p className="text-3xl font-bold text-red-400">{formatCurrency(resumen.deuda_pendiente)}</p>
              <p className="text-sm text-red-300">{(100 - resumen.tasa_cobro).toFixed(1)}% del total</p>
            </div>
          </div>
          
          {/* Barra de progreso global */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Progreso de Recuperación</span>
              <span className="font-medium">{resumen.tasa_cobro}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-4">
              <div 
                className="h-4 rounded-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-1000"
                style={{ width: `${resumen.tasa_cobro}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EstadisticasMorosidad;
